import React from "react";
import { ErrorMessage, Field } from "formik";
import { formatUnderscoreToSpace } from "../../utils/text-format";
import { Check, X } from "lucide-react";

export const FormCheckboxField = ({
  name,
  label,
  labelClassName = "",
  wrapperClassName = "",
}) => {
  return (
    <Field name={name} type="checkbox">
      {({ field, meta }) => {
        const hasError = meta.touched && meta.error;

        return (
          <div className={`flex flex-col gap-1 ${wrapperClassName}`}>
            <label
              htmlFor={name}
              className="inline-flex items-center gap-2.5 cursor-pointer select-none group"
            >
              <input
                type="checkbox"
                id={name}
                {...field}
                className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-2 focus:ring-indigo-500/20 focus:ring-offset-0 transition-all cursor-pointer accent-primary"
              />
              {label && (
                <span
                  className={`text-sm font-bold tracking-wide uppercase transition-colors ${
                    hasError ? "text-red-500" : "text-slate-700"
                  } ${labelClassName}`}
                >
                  {formatUnderscoreToSpace(label)}
                </span>
              )}
            </label>

            <div className="min-h-[1.25rem] ps-6">
              <ErrorMessage name={name}>
                {(msg) => (
                  <span className="text-xs font-medium text-red-500 block">
                    {msg}
                  </span>
                )}
              </ErrorMessage>
            </div>
          </div>
        );
      }}
    </Field>
  );
};

export const FormCheckboxGroupField = ({
  name = "",
  label = "",
  options = [],
  labelClassName = "",
  wrapperClassName = "",
}) => {
  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        const hasError = meta.touched && meta.error;
        const fieldValue = Array.isArray(field.value) ? field.value : [];

        return (
          <div className={`w-full flex flex-col gap-2 ${wrapperClassName}`}>
            {label && (
              <label
                className={`text-sm font-bold tracking-wide uppercase ${
                  hasError ? "text-red-500" : "text-slate-700"
                } ${labelClassName}`}
              >
                {formatUnderscoreToSpace(label)}
              </label>
            )}

            <div className="flex flex-wrap gap-3 p-3 bg-slate-50/50 border border-slate-200/80 rounded-xl">
              {options.map((option) => {
                const isChecked = fieldValue.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-lg shadow-2xs hover:border-slate-300 transition-all cursor-pointer text-xs font-semibold text-slate-700"
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        const nextValue = [...fieldValue];
                        if (e.target.checked) {
                          nextValue.push(option.value);
                        } else {
                          const idx = nextValue.indexOf(option.value);
                          if (idx > -1) nextValue.splice(idx, 1);
                        }
                        form.setFieldValue(name, nextValue);
                      }}
                      className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-0 accent-primary cursor-pointer"
                    />
                    {option.label}
                  </label>
                );
              })}
            </div>

            <div className="min-h-[1.25rem] ps-1">
              <ErrorMessage name={name}>
                {(msg) => (
                  <span className="text-xs font-medium text-red-500 block">
                    {msg}
                  </span>
                )}
              </ErrorMessage>
            </div>
          </div>
        );
      }}
    </Field>
  );
};

export const FormToggleSwitch = ({
  name,
  label,
  value = false,
  disabled = false,
  labelClassName = "",
  wrapperClassName = "",
  formatUnderscoreToSpace = (text) => text?.replace(/_/g, " "),
}) => {
  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        // Enforce strict boolean resolution
        const isChecked = Boolean(field.value === true || field.value === "on");
        const hasError = Boolean(meta.touched && meta.error);

        const handleChange = (e) => {
          // Explicitly set boolean value in Formik state instead of string "on"/"off"
          form.setFieldValue(name, e.target.checked);
        };

        return (
          <div className={`flex flex-col gap-1.5 ${wrapperClassName}`}>
            <label
              htmlFor={name}
              className={`group inline-flex items-center gap-3 select-none ${
                disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
              }`}
            >
              {/* Toggle Track */}
              <div className="relative inline-flex items-center">
                <input
                  type="checkbox"
                  id={name}
                  name={name}
                  checked={isChecked}
                  disabled={disabled}
                  onChange={handleChange}
                  onBlur={field.onBlur}
                  className="sr-only peer"
                />
                <div
                  className={`
                    h-6 w-11 rounded-full transition-colors duration-200 ease-in-out
                    peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-500 peer-focus-visible:ring-offset-2
                    ${
                      isChecked
                        ? "bg-primary dark:bg-indigo-500"
                        : "bg-gray-200 dark:bg-gray-700"
                    }
                    ${hasError ? "ring-2 ring-red-500/50" : ""}
                  `}
                />

                {/* Switch Knob & Icons */}
                <div
                  className={`
                    absolute start-0.5 top-0.5 flex h-5 w-5 items-center justify-center 
                    rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out
                    dark:bg-gray-100
                    ${isChecked ? "translate-x-5" : "translate-x-0"}
                  `}
                >
                  {isChecked ? (
                    <Check className="h-3 w-3 stroke-[3] text-primary" />
                  ) : (
                    <X className="h-3 w-3 stroke-[3] text-gray-400" />
                  )}
                </div>
              </div>

              {/* Label */}
              {label && (
                <span
                  className={`text-xs font-semibold uppercase tracking-wider transition-colors ${
                    hasError ? "text-red-500" : "text-gray-700"
                  } ${labelClassName}`}
                >
                  {formatUnderscoreToSpace(label)}
                </span>
              )}
            </label>

            {/* Error Message Container */}
            <div className="min-h-[1.25rem] px-0.5">
              <ErrorMessage name={name}>
                {(msg) => (
                  <span className="block text-xs font-medium text-red-500 dark:text-red-400">
                    {msg}
                  </span>
                )}
              </ErrorMessage>
            </div>
          </div>
        );
      }}
    </Field>
  );
};

export const ToggleSwitch = ({
  label,
  value = false,
  onChange,
  labelClassName = "",
  wrapperClassName = "",
}) => {
  return (
    <div className={`flex items-center ${wrapperClassName}`}>
      <label className="inline-flex items-center gap-3 cursor-pointer select-none">
        <div className="relative inline-flex items-center">
          <input
            type="checkbox"
            checked={value}
            onChange={(e) => onChange(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary shadow-2xs" />
        </div>

        {label && (
          <span
            className={`text-sm font-bold tracking-wide uppercase text-slate-700 ${labelClassName}`}
          >
            {formatUnderscoreToSpace(label)}
          </span>
        )}
      </label>
    </div>
  );
};
